/**
 * Centralized registry for all test IDs used across the application.
 * This provides type safety, consistency, and IntelliSense support.
 */

import { type TenantUserColumnIds } from '../TenantUser'

// Agency related test IDs
export const AgencyTestIds = {
  // List items
  list: (id: string) => `agency-list-${id}`,
  /** The name of the agency in the list */
  listName: (id: string) => `agency-list-${id}-name`,

  // Actions
  actions: (id: string) => `other-agency-actions-for-${id}`,
  addActions: (id: string) => `add-agency-actions-for-${id}`,

  // Details
  details: {
    contactAddress: 'agency-details-contact-address',
    contactCountryName: 'agency-details-contact-country-name',
    contactEmail: 'agency-details-contact-email',
    contactPhoneNumber: 'agency-details-contact-phone-number',
    contactMobilePhone: 'agency-details-contact-mobile-phone',
    parentGroupName: 'agency-details-parent-group-name',
    linkedCompanyName: 'agency-details-linked-company-name'
  },

  layout: {
    actionsDropdown: 'agency-details-actions-dropdown',
    tabs: {
      general: 'agency-details-general-tab',
      ports: 'agency-details-ports-tab',
      dangerZone: 'agency-details-danger-zone-tab'
    }
  },

  // Actions
  action: {
    addAgency: 'add-agency-action',
    addGroup: 'add-agency-group-action',
    changeName: 'change-name-action',
    changeAgencyContactInfo: 'change-agency-contact-info-btn',
    linkToOtherCompany: 'link-to-other-company-action',
    moveAgencyNode: 'move-agency-node-action',
    removeGroup: 'remove-group-action',
    deactivateAgency: 'deactivate-agency-action',
    reactivateAgency: 'reactivate-agency-btn'
  },

  // Modals
  modal: {
    addAgency: {
      nameInput: 'add-agency-name-input',
      shortnameInput: 'add-agency-shortname-input'
    },
    addAgencyGroup: {
      nameInput: 'add-agency-group-name-input',
      shortnameInput: 'add-agency-group-shortname-input'
    },
    changeName: {
      nameInput: 'agency-group-name-input',
      shortNameInput: 'agency-short-name-input'
    },
    changeContactInfo: {
      addressInput: 'change-agency-contact-info-address-input',
      emailInput: 'change-agency-contact-info-email-input',
      phoneInput: 'change-agency-contact-info-phone-input',
      mobileInput: 'change-agency-contact-info-mobile-input'
    }
  },

  // Structure
  structure: {
    views: {
      agencyStructure: 'agency-structure-view',
      deactivatedAgencies: 'deactivated-agencies-view'
    }
  }
}

// Common UI test IDs
export const CommonTestIds = {
  dialog: {
    close: 'dialog-close',
    closeX: 'dialog-close-x',
    saveButton: 'modal-save-button'
  },
  page: {
    title: 'page-title',
    description: 'page-description'
  },
  select: {
    database: 'select-database-input',
    agencyGroup: 'select-agency-group-input',
    agency: 'select-agency-input',
    company: 'select-company-input',
    country: 'select-country-input',
    currency: 'select-currency-input'
  },
  combobox: {
    item: (id: string) => `combobox-item-${id}`
  }
}

// Company test IDs
export const CompanyTestIds = {
  addButton: 'add-company-btn',
  companyCode: 'company-code',
  list: {
    row: (companyCode: string) => `company-row-${companyCode}`,
    actionDropdown: (companyCode: string) =>
      `company-actions-for-${companyCode}`
  },
  actions: {
    edit: 'edit-company-action',
    deactivate: 'deactivate-company-action',
    activate: 'reactivate-company-action'
  },
  modal: {
    nameInput: 'add-company-input-name',
    descriptionInput: 'add-company-input-description'
  },
  companiesList: {
    tabs: {
      active: 'active-tab',
      inactive: 'inactive-tab'
    }
  }
}

// Tenant users test IDs
export const TenantUserTestIds = {
  table: {
    views: {
      active: 'active-tab',
      inactive: 'inactive-tab'
    },
    cell: (userId: string, columnId: TenantUserColumnIds) =>
      `tenant-user-cell-${userId}-${columnId}`
  },
  details: {
    userName: 'tenant-user-details-name',
    userTitle: 'tenant-user-details-title',
    userEmail: 'tenant-user-details-email',
    userPhone: 'tenant-user-details-phone'
  },
  databaseAccessTable: {
    grantOrRevokeButton: (databaseId: string) =>
      `grant-or-revoke-database-access-action-for-${databaseId}`
  },
  actions: {
    addTenantUser: 'add-tenant-user-action',
    changeName: 'change-name-action',
    changeTitle: 'change-title-action',
    changeEmail: 'change-email-action',
    changePhone: 'change-phone-action',
    changeProfileImage: 'change-profile-image-action',
    deactivateUser: 'deactivate-user-action',
    scheduleUserDeactivation: 'schedule-user-deactivation-action',
    activateUser: 'activate-user-action',
    scheduleUserActivation: 'schedule-user-activation-action',
    grantTenantAdminPrivileges: 'grant-tenant-admin-privileges-action',
    revokeTenantAdminPrivileges: 'revoke-tenant-admin-privileges-action',
    cancelDeactivationSchedule: 'remove-deactivation-schedule-action',
    cancelActivationSchedule: 'remove-activation-schedule-action'
  },
  forms: {
    changeJobTitle: {
      titleInput: 'change-title-input-title'
    }
  },
  modal: {
    add: {
      userId: 'add-tenant-user-user-id',
      fullNameInput: 'add-tenant-user-input-full-name',
      initialsInput: 'add-tenant-user-input-initials',
      titleInput: 'add-tenant-user-input-title',
      emailInput: 'add-tenant-user-input-email',
      mobilePhoneInput: 'add-tenant-user-input-mobile-phone'
    },
    changeName: {
      fullNameInput: 'change-name-input-full-name',
      initialsInput: 'change-name-input-initials'
    },
    changePhoneNumber: {
      mobilePhoneInput: 'change-phone-number-input-mobile-phone'
    },
    scheduleDeactivation: {
      deactivationDateInput:
        'schedule-user-deactivation-input-deactivation-date',
      saveButton: 'schedule-user-deactivation-save-button'
    },
    scheduleActivation: {
      activationDateInput: 'schedule-user-activation-input-activation-date',
      saveButton: 'schedule-user-activation-save-button'
    }
  }
}

// Access role test IDs
export const AccessRoleTestIds = {
  actions: {
    add: 'add-access-role-action',
    changeName: 'change-access-role-name-action',
    delete: 'delete-access-role-action',
    actionDropdown: (accessRoleId: string) =>
      `access-role-actions-for-${accessRoleId}`
  }
}

// Support test IDs
export const SupportTestIds = {
  actions: {
    grantSupportAccess: 'grant-support-access-action',
    startSupportSession: 'start-support-session-action',
    endSupportSession: 'end-support-session-action',
    moreDetails: 'more-details-action'
  },
  modal: {
    createSession: {
      supportPersonnelInput: 'add-support-personnel-input-support-personnel'
    }
  }
}

// Stakeholders test IDs
export const StakeholdersTestIds = {
  contacts: {
    actions: {
      addDropdown: 'add-contacts-dropdown-btn',
      addContactPerson: 'add-contact-person-action',
      addContactPoint: 'add-contact-point-action',
      deleteContactPerson: 'delete-contact-person-action',
      editContactPoint: 'edit-contact-point-action',
      editContactPerson: 'edit-contact-person-action',
      showContactDetails: 'show-contact-details-action'
    },
    list: {
      contactPointId: (contactPointId: string) =>
        `contact-point-id-${contactPointId}`,
      actions: {
        dropdownBtn: (contactId: string) =>
          `contact-person-actions-dropdown-btn-${contactId}`,
        showContactDetails: 'show-contact-details-action',
        editContactPerson: 'edit-contact-person-action',
        deleteContactPerson: 'delete-contact-person-action'
      },
      contactPersonFirstName: (name: string) =>
        `contact-person-first-name-${name}`,
      contactPersonJobTitle: (jobTitle: string) =>
        `contact-person-job-title-${jobTitle}`,
      contactPersonDepartment: (department: string) =>
        `contact-person-department-${department}`,
      contactPersonEmail: (email: string) => `contact-person-email-${email}`,
      contactPersonMobilePhone: (mobilePhone: string) =>
        `contact-person-mobile-phone-${mobilePhone}`,
      contactPersonDirectLine: (directLine: string) =>
        `contact-person-direct-line-${directLine}`,
      contactPersonLastName: (lastName: string) =>
        `contact-person-last-name-${lastName}`,
      contactPersonId: (contactPersonId: string) =>
        `contact-person-id-${contactPersonId}`
    }
  },
  overview: {
    tabs: {
      quickSearch: 'quick-search-tab',
      advancedSearch: 'advanced-search-tab'
    },
    quickSearchTab: {
      input: 'quick-search-input'
    },
    searchResultItem: {
      id: (stakeholderId: string) => `search-result-item-${stakeholderId}`
    }
  },
  forms: {
    contactPersonInfo: {
      otherNameInput: 'form-stakeholder-contact-person-info-other-name-input',
      emailInput: 'form-stakeholder-contact-person-info-email-input',
      mobilePhoneInput:
        'form-stakeholder-contact-person-info-mobile-phone-input',
      directLineInput: 'form-stakeholder-contact-person-info-direct-line-input',
      lastNameInput: 'form-stakeholder-contact-person-info-last-name-input',
      firstNameInput: 'form-stakeholder-contact-person-info-first-name-input',
      jobTitleInput: 'form-stakeholder-contact-person-info-job-title-input',
      departmentInput: 'form-stakeholder-contact-person-info-department-input'
    },
    generalInfo: {
      nameInput: 'form-stakeholder-general-info-name-input',
      shortNameInput: 'form-stakeholder-general-info-short-name-input',
      emailInput: 'form-stakeholder-general-info-email-input',
      phoneInput: 'form-stakeholder-general-info-phone-input',
      countryInput: 'form-stakeholder-general-info-country-input'
    },
    internalIdentificationNumbers: {
      clientNumberInput:
        'form-stakeholder-identification-numbers-client-number-input',
      vendorNumberInput:
        'form-stakeholder-identification-numbers-vendor-number-input'
    },
    officialBusinessNumbers: {
      taxIdentificationNumberInput:
        'form-stakeholder-official-business-numbers-tax-identification-number-input',
      eoriNumberInput:
        'form-stakeholder-official-business-numbers-eori-number-input'
    },
    postalAddress: {
      addressInput: 'form-stakeholder-postal-address-address-input'
    },
    visitAddress: {
      addVisitAddressButton: 'add-visit-address-btn',
      removeVisitAddressButton: 'remove-visit-address-btn',
      sameAsPostalRadio: 'form-stakeholder-visit-address-same-as-postal-radio',
      customVisitAddressRadio:
        'form-stakeholder-visit-address-custom-visit-address-radio',
      addressInput: 'form-stakeholder-visit-address-address-input'
    },
    billingAddress: {
      sameAsPostalRadio:
        'form-stakeholder-billing-address-same-as-postal-radio',
      customBillingAddressRadio:
        'form-stakeholder-billing-address-custom-billing-address-radio',
      addressInput: 'form-stakeholder-billing-address-address-input'
    },
    billingDetails: {
      invoiceEmailInput: 'form-stakeholder-billing-details-invoice-email-input',
      defaultInvoiceCurrencyRadio:
        'form-stakeholder-billing-details-default-invoice-currency-radio',
      customInvoiceCurrencyRadio:
        'form-stakeholder-billing-details-custom-invoice-currency-radio',
      invoiceCurrencyInput:
        'form-stakeholder-billing-details-invoice-currency-input',
      defaultPaymentTermsRadio:
        'form-stakeholder-billing-details-default-payment-terms-radio',
      customPaymentTermsRadio:
        'form-stakeholder-billing-details-custom-payment-terms-radio',
      creditDaysInput: 'form-stakeholder-billing-details-credit-days-input',
      useCreditRunningMonthCheckbox:
        'form-stakeholder-billing-details-use-credit-running-month-checkbox'
    }
  },
  sections: {
    generalInfo: {
      name: 'general-info-name',
      shortName: 'general-info-short-name',
      email: 'general-info-email',
      phoneNumber: 'general-info-phone-number',
      country: 'general-info-country'
    },
    internalIdentificationNumbers: {
      vendorNumber: 'internal-identification-numbers-vendor-number',
      clientNumber: 'internal-identification-numbers-client-number'
    },
    officialBusinessNumbers: {
      taxIdentificationNumber:
        'official-business-numbers-tax-identification-number',
      eoriNumber: 'official-business-numbers-eori-number'
    },
    postalAddress: {
      address: 'postal-address-address',
      country: 'postal-address-country'
    },
    visitAddress: {
      address: 'visit-address-address',
      country: 'visit-address-country'
    }
  },
  actions: {
    addStakeholderDropdown: 'add-stakeholder-dropdown-btn',
    restoreStakeholder: 'restore-stakeholder-action',
    editGeneral: 'edit-general-action',
    changePostAddress: 'change-post-address-action',
    changeVisitAddress: 'change-visit-address-action',
    changeBillingAddress: 'change-billing-address-action',
    removePostalAddress: 'remove-postal-address-action',
    removeVisitAddress: 'remove-visit-address-action',
    addVendor: 'add-vendor-action',
    addClient: 'add-client-action',
    addOtherParty: 'add-other-party-action',
    changeInternalIdentificationNumbers:
      'change-internal-identification-numbers-action',
    changeOfficialBusinessNumbers: 'change-official-business-numbers-action',
    updateBillingInformation: 'update-billing-information-action'
  }
}

// Currency test IDs
export const CurrencyTestIds = {
  list: {
    currencyCode: (currencyCode: string) => `currency-code-${currencyCode}`
  },
  action: {
    addCurrency: 'add-currency-action',
    addCurrencyToSystem: (currencyCode: string) =>
      `add-currency-to-system-action-for-${currencyCode}`,
    removeCurrency: 'remove-currency-action'
  }
}

// Ports test IDs
export const PortTestIds = {
  list: {
    portRow: (locationCode: string) => `port-row-${locationCode}`
  }
}

// Global search test IDs
export const GlobalSearchTestIds = {
  dialog: 'global-search-dialog',
  input: 'global-search-input',
  tagBadge: 'global-search-tag-badge',
  clearButton: 'global-search-clear-button',
  searching: 'global-search-searching',
  noResults: 'global-search-no-results',
  groupHeader: (context: string) => `global-search-group-${context}`,
  item: {
    recent: (query: string) => `global-search-recent-${query}`,
    context: (context: string) => `global-search-context-${context}`,
    result: (id: string) => `global-search-result-${id}`,
    showAll: (context: string) => `global-search-show-all-${context}`
  }
}

// Helper function to create test IDs with dynamic values
export function createTestId(
  baseId: string,
  ...parts: (string | number)[]
): string {
  return [baseId, ...parts].join('-')
}

// Main test ID registry
export const testIds = {
  agency: AgencyTestIds,
  common: CommonTestIds,
  company: CompanyTestIds,
  tenantUser: TenantUserTestIds,
  support: SupportTestIds,
  accessRole: AccessRoleTestIds,
  stakeholders: StakeholdersTestIds,
  currency: CurrencyTestIds,
  stakeholder: StakeholdersTestIds,
  port: PortTestIds,
  globalSearch: GlobalSearchTestIds
}
